import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { GraduationCap, Heart, Stethoscope, ArrowRight } from 'lucide-react';

const audiences = [
  {
    icon: <GraduationCap size={22} strokeWidth={1.5} />,
    tag: 'For Students',
    title: 'Discover Your Direction',
    tagline: 'Understand who you are. Align your path with your strengths.',
    points: [
      'Uncover your cognitive & emotional strengths',
      'Get stream, subject, and career alignment recommendations',
      'Explore future skills, college pathways, and career roadmaps',
      'Build confidence through structured insight',
    ],
    color: '#2A9D8F',
    bg: '#EDF7F5',
    image: 'https://static.prod-images.emergentagent.com/jobs/6caceefd-cb80-4c8a-a8fe-90917c71e43e/images/50f422626889f84337d3114cff30c7094f5e74829596418a8e65accc6ff875cc.png',
  },
  {
    icon: <Heart size={22} strokeWidth={1.5} />,
    tag: 'For Parents',
    title: 'Understand Your Child Better',
    tagline: 'Make informed decisions guided by science and human expertise.',
    points: [
      'Clear, jargon-free career and psychological reports',
      'Identify development needs early',
      'Structured guidance for stream, subject, college, and life choices',
    ],
    color: '#F4A261',
    bg: '#FEF3E8',
    image: 'https://images.unsplash.com/photo-1758598737516-ffd38ba74838?crop=entropy&cs=srgb&fm=jpg&q=80&w=600',
  },
  {
    icon: <Stethoscope size={22} strokeWidth={1.5} />,
    tag: 'For Counselors',
    title: 'Join a Trusted Platform',
    tagline: 'Support students through structured, technology-assisted guidance.',
    points: [
      'Manage assignments and case tracking',
      'Access AI-generated pre-session career and assessment reports',
      'Be part of a credible, growing network',
    ],
    color: '#8DA399',
    bg: '#F0F4F2',
    image: 'https://static.prod-images.emergentagent.com/jobs/6caceefd-cb80-4c8a-a8fe-90917c71e43e/images/8acca74bf3a0a6544771c02256167b14063e4158ad80c9b3ebf8b0fc06b76753.png',
  },
];

const WhoIsItFor = ({ onOpenAssessment, onOpenJoin }) => {
  const [ref, visible] = useScrollReveal();
  const handleAudienceCta = (index) => {
    if (index === 2) {
      onOpenJoin?.();
      return;
    }
    onOpenAssessment?.();
  };

  return (
    <section
      ref={ref}
      style={{ padding: '100px 24px', background: 'rgba(240,244,242,0.5)' }}
      data-testid="who-for-section"
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div className={`reveal ${visible ? 'visible' : ''}`} style={{ textAlign: 'center', marginBottom: '64px' }}>
          <div className="section-label">Who Is It For</div>
          <div className="section-divider" />
          <h2 style={{
            fontFamily: 'Manrope', fontWeight: 700, fontSize: 'clamp(30px,3.8vw,44px)',
            color: '#1A202C', margin: '20px 0 16px', letterSpacing: '-0.02em',
          }}>
            Designed for Every<br />
            <span style={{ fontWeight: 300, color: '#64748B' }}>Member of the Journey</span>
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
          {audiences.map((a, i) => (
            <div
              key={a.title}
              className={`card-lift reveal reveal-delay-${i + 1} ${visible ? 'visible' : ''}`}
              data-testid={`audience-card-${a.tag.toLowerCase().replace(/\s/g, '-')}`}
              style={{
                borderRadius: '24px',
                overflow: 'hidden',
                background: 'rgba(255,255,255,0.85)',
                border: '1px solid rgba(255,255,255,0.8)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.05)',
              }}
            >
              {/* Image */}
              <div style={{ height: '200px', overflow: 'hidden', position: 'relative' }}>
                <img
                  src={a.image}
                  alt={`${a.title} - MindVrik career guidance`}
                  loading="lazy"
                  decoding="async"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', transition: 'transform 0.5s ease' }}
                  onMouseEnter={e => e.target.style.transform = 'scale(1.05)'}
                  onMouseLeave={e => e.target.style.transform = 'scale(1)'}
                />
                {/* Overlay gradient */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: `linear-gradient(to bottom, transparent 50%, ${a.bg}99)`,
                }} />
                {/* Tag badge */}
                <div style={{
                  position: 'absolute', top: '16px', left: '16px',
                  background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(8px)',
                  borderRadius: '99px', padding: '6px 14px',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}>
                  <span style={{ color: a.color }}>{a.icon}</span>
                  <span style={{ fontSize: '12px', fontFamily: 'Manrope', fontWeight: 600, color: '#1A202C' }}>{a.tag}</span>
                </div>
              </div>

              {/* Content */}
              <div style={{ padding: '28px' }}>
                <h3 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '20px', color: '#1A202C', margin: '0 0 8px' }}>{a.title}</h3>
                <p style={{ fontSize: '14px', color: '#64748B', margin: '0 0 20px', lineHeight: 1.6 }}>{a.tagline}</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px' }}>
                  {a.points.map(pt => (
                    <li key={pt} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: a.color, marginTop: '7px', flexShrink: 0 }} />
                      <span style={{ fontSize: '13px', color: '#4A5568', lineHeight: 1.55, fontFamily: 'Inter' }}>{pt}</span>
                    </li>
                  ))}
                </ul>
                <button style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  color: a.color, fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px',
                  padding: 0, transition: 'gap 0.2s ease'
                }}
                  onMouseEnter={e => e.currentTarget.style.gap = '10px'}
                  onMouseLeave={e => e.currentTarget.style.gap = '6px'}
                  onClick={() => handleAudienceCta(i)}
                  data-testid={`audience-cta-${i}`}
                >
                  Learn More <ArrowRight size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhoIsItFor;
